import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router';

// API/Components
import {
	ANALYSIS_RESULT,
	DATA_ERROR_VALUE,
	GRAPH_BOUNDS,
	GRAPH_STYLE,
	isEmpty,
	validValue,
	AnalysisResultToStateColor,
	formatDateTime,
	measUnitsConversion,
	getUserTempUnits
} from './enums';
import { DrawHorizontalDashedLine, DrawVerticalDashedLineAtTooltip } from './chart_helpers';

// Material UI
import Button from '@mui/material/Button';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';
import Grid from '@mui/material/Grid';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import {
	Chart as ChartJS,
	LinearScale,
	CategoryScale,
	BarElement,
	PointElement,
	LineElement,
	Legend,
	Tooltip,
	Filler,
	LineController,
	ScatterController,
} from 'chart.js';
import { Chart } from 'react-chartjs-2';
import zoomPlugin from 'chartjs-plugin-zoom';

// Register Chart.JS Plugins
ChartJS.register(
	LinearScale,
	CategoryScale,
	BarElement,
	PointElement,
	LineElement,
	Legend,
	Tooltip,
	Filler,
	LineController,
	ScatterController,
	zoomPlugin,
);


export default function SystemTrendGraph(props) {
	const { trendData, limitData, graphScalesData, graphType, loading, noCurrentMeasurement } = props;

	const { locationNum } = useParams();
	
	const chartRef = useRef(null);
	
	const [graphData, setGraphData] = useState([]);
	const [graphSettings, setGraphSettings] = useState({
		limitsEnabled: true,
	});
	const [graphStyle, setGraphStyle] = useState(GRAPH_STYLE.AREA);
	const [graphOptions, setGraphOptions] = useState({
		maintainAspectRatio: false,
		animation: false,
		transitions: {
			zoom: {
				animation: {
					duration: 0,
				},
			},
		},
		plugins: {
			legend: {
				display: false,
			},
			tooltip: {
				intersect: false,
				// axis: 'x',
				mode: 'index',
			},
			zoom: {
				pan: {
					enabled: true,
					mode: 'xy',
				},
				limits: {
					x: {
						minRange: 2,
					},
					y: {
						min: 0,
						max: 100,
						minRange: 5,
					}
				},
				zoom: {
					mode: 'y',
					wheel: {
						enabled: true,
						speed: 0.02,
					},
					pinch: {
						enabled: true,
					},
					onZoomStart: (e) => {
						if (typeof e.chart.config?.options?.plugins?.zoom?.zoom === "undefined") {
							return;
						}
						if (e.event.shiftKey === true) {
							e.chart.config.options.plugins.zoom.zoom.mode = "x";
							e.chart.config.options.plugins.zoom.zoom.scaleMode = "x";
						} else {
							e.chart.config.options.plugins.zoom.zoom.mode = "y";
							e.chart.config.options.plugins.zoom.zoom.scaleMode = "y";
						}
					},
				}
			},
		},
	});
	
	// const [isLoading, setIsLoading] = useState(loading);

	// Keep track of the limits data for drawing the limit lines
	const limitDataRef = useRef(null);
	useEffect(() => {
		limitDataRef.current = limitData;
	}, [limitData]);
	
	// Keep track of the graph type for drawing limit lines
	const graphTypeRef = useRef(null);
	useEffect(() => {
		graphTypeRef.current = graphType;
	}, [graphType]);
	
	// Keep track of the graph settings for drawing limit lines
	const graphSettingsRef = useRef(null);
	useEffect(() => {
		graphSettingsRef.current = graphSettings;
	}, [graphSettings]);

	// Update the graph data sets
	useEffect(() => {
		if (typeof trendData != "undefined" && trendData !== null && !isEmpty(trendData)) {

			const systemData = GetGraphData(trendData, graphType);
			if (systemData !== undefined && systemData !== null) {
				setGraphData(systemData);
			}
		}
	}, [trendData, graphType, graphSettings, graphStyle, limitData]);

	useEffect(() => {
		const graphScales = SetGraphYScale(graphType);
		if (graphScales !== undefined && graphScales !== null) {
			setGraphOptions((prevState) => {
				return {
					...prevState,
					scales: {
						y: graphScales,
						x: {
							title: {
								display: false,
							}
						}
					}
				}
			});
		}
	}, [graphScalesData, graphType]);
	
	useEffect(() => {
		SetGraphBounds();
	}, [graphType]);

	const graphPlugins = [
		{
			afterDraw: chart => DrawVerticalDashedLineAtTooltip(chart)
		},
		{
			afterDatasetDraw: chart => {
				let graphType = graphTypeRef.current;
				let graphSettings = graphSettingsRef.current;
				let limitData = limitDataRef.current;

				if (limitData != undefined && limitData != null && !isEmpty(limitData)) {
					// If enabled, draw a horizontal dashed line at the various limit values
					if (graphSettings.limitsEnabled) {
						const limits = GetLimitsData(limitData, graphType);
						if (limits != undefined && limits != null && limits.length > 0) {
							limits.forEach((limit) => {
								DrawHorizontalDashedLine(chart, limit.value, limit.color);
							});
						}
					}
				}
			}
		}
	];

	function SetGraphBounds() {
		let graphZoom = GRAPH_BOUNDS.SYSTEM_VOLTAGE;
		switch(graphType) {
			case 0:  // Voltage
				graphZoom = GRAPH_BOUNDS.SYSTEM_VOLTAGE;
				break;

			case 1:  // Current
				graphZoom = GRAPH_BOUNDS.FLOAT_CURRENT;
				break;

			case 2:  // Temperature
				graphZoom = {
					...GRAPH_BOUNDS.TEMPERATURE,
					bounds_max: measUnitsConversion(GRAPH_BOUNDS.TEMPERATURE.bounds_max),
					bounds_min: measUnitsConversion(GRAPH_BOUNDS.TEMPERATURE.bounds_min),
				};
				break;

			default:
				graphZoom = GRAPH_BOUNDS.SYSTEM_VOLTAGE;
				break;
		}
		
		setGraphOptions((prevState) => {
			return {
				...prevState,
				plugins: {
					...prevState.plugins,
					zoom: {
						...prevState.plugins.zoom,
						limits: {
							...prevState.plugins.zoom.limits,
							y: {
								max: graphZoom.bounds_max,
								min: graphZoom.bounds_min,
								minRange: graphZoom.min_range
							},
						},
					},
				},
			}
		});
	}

	function GetLimitsData(limitData, graphType) {
		let critHiLimit = DATA_ERROR_VALUE.INVALID_VALUE;
		let critLoLimit = DATA_ERROR_VALUE.INVALID_VALUE;
		
		if (limitData !== undefined && limitData != null && !isEmpty(limitData) &&
			graphType !== undefined && graphType != null) {
			switch(graphType) {
				case 0:
					critHiLimit = limitData.volt.hi_crit;
					critLoLimit = limitData.volt.lo_crit;
					break;
				case 1:
					critHiLimit = limitData.current.system;
					break;
				case 2:
					critHiLimit = measUnitsConversion(limitData.temp.hi_crit);
					critLoLimit = measUnitsConversion(limitData.temp.lo_crit);
					break;
				default:
					critHiLimit = limitData.volt.hi_crit;
					critLoLimit = limitData.volt.lo_crit;
					break;
			}
		}

		// Get the associated limit colors
		let hiCritColor = AnalysisResultToStateColor(ANALYSIS_RESULT.ANALYSIS_ABOVE_LIMITS_CRITICAL);
		let loCritColor = AnalysisResultToStateColor(ANALYSIS_RESULT.ANALYSIS_BELOW_LIMITS_CRITICAL);

		let limitSets = [];
		if (critHiLimit !== undefined && critHiLimit > DATA_ERROR_VALUE.INVALID_VALUE) {
			limitSets.push({
				value: critHiLimit,
				color: hiCritColor
			});
		}

		if (critLoLimit !== undefined && critLoLimit > DATA_ERROR_VALUE.INVALID_VALUE) {
			limitSets.push({
				value: critLoLimit,
				color: loCritColor
			});
		}
		
		return limitSets;
	}

	function SetGraphYScale(graphType) {
		
		if (graphScalesData !== undefined && graphScalesData !== null && !isEmpty(graphScalesData)) {

			const scalesData = graphScalesData.system;

			let max = DATA_ERROR_VALUE.INVALID_VALUE;
			let min = DATA_ERROR_VALUE.INVALID_VALUE;
			let title = "";
			
			switch (graphType) {
				case 0:  // Voltage
					max = validValue(scalesData.voltage?.max, GRAPH_BOUNDS.SYSTEM_VOLTAGE.default_max);
					min = validValue(scalesData.voltage?.min, GRAPH_BOUNDS.SYSTEM_VOLTAGE.default_min);
					title = "System Voltage (VDC)";
					break;

				case 1:  // Current
					max = validValue(scalesData.current?.float_max, GRAPH_BOUNDS.FLOAT_CURRENT.default_max);
					min = GRAPH_BOUNDS.FLOAT_CURRENT.default_min;
					title = "System Current (A)";
					break;

				case 2:  // Temperature
					const maxC = validValue(scalesData.temperature?.max, GRAPH_BOUNDS.TEMPERATURE.default_max);
					const minC = validValue(scalesData.temperature?.min, GRAPH_BOUNDS.TEMPERATURE.default_min);
					max = measUnitsConversion(maxC);
					min = measUnitsConversion(minC);
					title = 'Ambient Temperature (' + getUserTempUnits() + ')';
					break;

				default:  // Voltage
					max = validValue(scalesData.voltage?.max, GRAPH_BOUNDS.SYSTEM_VOLTAGE.default_max);
					min = validValue(scalesData.voltage?.min, GRAPH_BOUNDS.SYSTEM_VOLTAGE.default_min);
					title = "System Voltage (VDC)";
					break;
			}

			if (max !== DATA_ERROR_VALUE.INVALID_VALUE && min !== DATA_ERROR_VALUE.INVALID_VALUE) {
				return {
					max: max,
					min: min,
					title: {
						display: true,
						text: title,
						font: {
							family: "Saira, sans-serif",
							size: 18,
							weight: 600,
						},
					},
				};
			}
		}

		return {};
	}

	function GetMeasurementInfo(measurement, graphType) {
		let value, analysis = 0;
		switch(graphType) {
			case 0:
				value = measurement.voltage.measurement?.toFixed(3);
				analysis = (measurement.voltage.analysis_valid) ? 
					measurement.voltage.analysis_result : 0;
				break;
			case 1:
				value = measurement.current.measurement?.toFixed(3);
				analysis = (measurement.current.analysis_valid) ? 
					measurement.current.analysis_result : 0;
				break;
			case 2:
				let temp = measurement.temperature.measurement?.toFixed(3);
				value = measUnitsConversion(temp);
				analysis = (measurement.temperature.analysis_valid) ? 
					measurement.temperature.analysis_result : 0;
				break;
			default:
				value = measurement.voltage.measurement?.toFixed(3);
				analysis = (measurement.voltage.analysis_valid) ? 
					measurement.voltage.analysis_result : 0;
				break;
		}
		value = (value !== null && value !== undefined && value > DATA_ERROR_VALUE.INVALID_VALUE) ? value : 0;
		let analysisColor = AnalysisResultToStateColor(analysis);

		return {value, analysisColor};
	}

	function GetGraphTypeText(graphType) {
		switch(graphType) {
			case 0:
				return "System Voltage Trend";
			case 1:
				return "System Current Trend";
			case 2:
				return "Ambient Temperature Trend";
			default:
				return "System Voltage Trend";
		}
	}
	
	function GetGraphData(trendData, graphType) {
		// Pick the units to graph based on the selected string
		if (trendData && trendData.length > 0) {
			
			const filteredData = trendData.map((measurement) => {
				const { value, analysisColor } = GetMeasurementInfo(measurement, graphType);
				
				return {
					label: formatDateTime(measurement.measurement_date),
					value: value,
					barColor: analysisColor + "77",  // Make the bar color partially transparent
					borderColor: analysisColor,  // The outline should be fully opaque
				};
			});

			const graphTypeText = GetGraphTypeText(graphType);
			
			// Add measurement data to datasets
			let dataSets = []
			dataSets.push({
				type: graphStyle === GRAPH_STYLE.POINT ? 'scatter' : 'line',
				label: graphTypeText,
				backgroundColor: filteredData.map((measurement) => measurement.barColor),
				borderColor: filteredData.map((measurement) => measurement.borderColor),
				segment: {
					borderColor: (ctx) => (filteredData[ctx.p1DataIndex].barColor),
					backgroundColor: (ctx) => (filteredData[ctx.p1DataIndex].barColor),
				},
				data: filteredData.map((measurement) => measurement.value),
				fill: graphStyle === GRAPH_STYLE.AREA,
				order: 10,
			});

			return {
				labels: filteredData.map((measurement) => measurement.label),
				datasets: dataSets,
			};
		}

		return null;
	}

	const handleLimitMarkersToggle = () => {
		setGraphSettings((prevState) => ({
			...prevState,
			limitsEnabled: !graphSettings.limitsEnabled})
		);
	};

	const handleGraphResetZoom = () => {
		chartRef.current.resetZoom();
	};

	const handleGraphStyle = (event, newStyle) => {
		if (newStyle !== null) {
			setGraphStyle(newStyle);
		}
	};

	return (
		<div
			style={{
				width: '100%',
				height: '99%',
				display: 'flex',
				flexDirection: 'column',
				position: 'relative',
			}}
		>
			<div style={{ height: '100%', width: '100%' }}>
				{ (graphData !== undefined && graphData.length !== 0) &&
					<Chart
						ref={chartRef}
						type='line'
						options={graphOptions}
						data={graphData}
						plugins = {graphPlugins}
					/>
				}
			</div>
			<Grid container spacing={1}>
				<Grid item xs alignItems="left">
					<ToggleButtonGroup
						value={[]}
						onChange={() => {}}
						aria-label="graph markers shown"
						size='small'
						sx={{ padding: '6px' }}
					>
						<ToggleButton
							value="limitMarkers"
							selected={graphSettings.limitsEnabled}
							onClick={handleLimitMarkersToggle}
						>
							Limits
						</ToggleButton>
					</ToggleButtonGroup>
				</Grid>
				<Grid item xs alignItems="center">
					<Button
						variant="contained"
						startIcon={<CenterFocusStrongIcon />}
						onClick={handleGraphResetZoom}
						sx={{
							marginY: 1,
							backgroundColor: 'white',
							border: '1px solid lightgray',
							color: 'black',
							":hover": {
								backgroundColor: 'lightgray',
								boxShadow: 'none',
							}

						}}
					>
						Reset View
					</Button>
				</Grid>
				<Grid item>
					<ToggleButtonGroup
						value={graphStyle}
						onChange={handleGraphStyle}
						exclusive
						aria-label="graph marker type"
						size='small'
						sx={{ padding: '6px' }}
					>
						<ToggleButton
							value={GRAPH_STYLE.AREA}
							aria-label="Area Graph Style"
						>
							Area
						</ToggleButton>
						<ToggleButton
							value={GRAPH_STYLE.LINE}
							aria-label="Line Graph Style"
						>
							Line
						</ToggleButton>
						<ToggleButton
							value={GRAPH_STYLE.POINT}
							aria-label="Point Graph Style"
						>
							Point
						</ToggleButton>
					</ToggleButtonGroup>
				</Grid>
			</Grid>
		</div>
	);
}